import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import { useAuth } from '../../../contexts/AuthContext';
import ApiDocumentationModal from '../components/ApiDocumentationModal';
import DataProcessingAgreement from '../components/DataProcessingAgreement';
import InspectionReportGenerator from '../components/InspectionReportGenerator';
import ComplianceAuditPackage from '../components/ComplianceAuditPackage';
import SafeIcon from '../../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { generateWebhookSecret, sendWebhook } from '../../../services/webhookService';

const { FiPlus, FiTrash2, FiEdit2, FiCheck, FiX, FiActivity, FiGlobe, FiAlertCircle, FiRefreshCw, FiCopy, FiCheckCircle, FiShield } = FiIcons;

const CompanySettings = ({ onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // FIKS: Legger til default empty array for webhooks for å hindre krasj hvis context ikke er oppdatert
  const { companies, webhooks = [], addWebhook, updateWebhook, deleteWebhook, updateCompany } = useData();

  // Find current company
  const currentCompany = companies.find(c => c.id === user?.companyId);

  const [activeTab, setActiveTab] = useState('general'); // 'general', 'dpa', 'api', 'webhooks'
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showApiDocs, setShowApiDocs] = useState(false);
  const [apiKey, setApiKey] = useState('');

  // Form state for general settings
  const [formData, setFormData] = useState({
    name: '',
    org_number: '',
    contact_email: '',
    notification_emails: [],
    branding: {
      primaryColor: '#145388',
      welcomeMessage: ''
    }
  });

  // Webhook Modal State
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [webhookForm, setWebhookForm] = useState({ url: '', events: [], active: true, secret: '' });
  const [testStatus, setTestStatus] = useState(null);

  const availableEvents = [
    { id: 'report.created', label: 'Nytt varsel opprettet' },
    { id: 'report.updated', label: 'Varsel oppdatert' },
    { id: 'investigation.created', label: 'Ny utredning startet' },
    { id: 'hms.incident', label: 'HMS-hendelse registrert' }
  ];

  useEffect(() => {
    if (currentCompany) {
      setApiKey(`pk_${currentCompany.id?.substring(0, 8) || 'test'}_${Math.random().toString(36).substring(2, 15)}`);
      setFormData({
        name: currentCompany.name || '',
        org_number: currentCompany.org_number || currentCompany.orgNumber || '',
        contact_email: currentCompany.contact_email || '',
        notification_emails: currentCompany.notification_emails || [],
        branding: currentCompany.branding || { primaryColor: '#145388', welcomeMessage: '' }
      });
      setLoading(false);
    } else {
      const timer = setTimeout(() => {
        setLoading(false);
        if (!apiKey) {
          setApiKey(`pk_demo_${Math.random().toString(36).substring(2, 15)}`);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [currentCompany]);

  const handleGeneralSave = async () => {
    setSaveLoading(true);
    try {
      await updateCompany(currentCompany.id, formData);
      alert('Innstillinger lagret!');
    } catch (err) {
      console.error(err);
      alert('Kunne ikke lagre innstillinger.');
    } finally {
      setSaveLoading(false);
    }
  };

  // Filter webhooks for this company (Sikker filtering med ?. og fallback)
  const companyWebhooks = Array.isArray(webhooks) ? webhooks.filter(wh => wh.companyId === user?.companyId) : [];

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/company/dashboard');
    }
  };

  const openWebhookModal = (webhook = null) => {
    if (webhook) {
      setEditingWebhook(webhook);
      setWebhookForm({
        url: webhook.url,
        events: webhook.events || [],
        active: webhook.active,
        secret: webhook.secret
      });
    } else {
      setEditingWebhook(null);
      setWebhookForm({
        url: '',
        events: ['report.created'],
        active: true,
        secret: generateWebhookSecret()
      });
    }
    setTestStatus(null);
    setShowWebhookModal(true);
  };

  const handleWebhookSubmit = (e) => {
    e.preventDefault();
    if (!webhookForm.url) return;

    if (editingWebhook) {
        if (typeof updateWebhook === 'function') updateWebhook(editingWebhook.id, webhookForm);
    } else {
        if (typeof addWebhook === 'function') addWebhook({ ...webhookForm, companyId: user?.companyId });
    }
    setShowWebhookModal(false);
  };

  const handleTestWebhook = async () => {
    if (!webhookForm.url) return;
    setTestStatus('loading');
    
    const payload = {
      id: 'test_evt_123',
      type: 'test.notification',
      timestamp: new Date().toISOString(),
      data: { message: 'Dette er en test-webhook fra HMS Varsling.' }
    };

    const result = await sendWebhook(webhookForm.url, 'test.notification', payload, webhookForm.secret);
    
    if (result.success) {
      setTestStatus('success');
    } else {
      setTestStatus('error');
    }
  };

  const toggleEvent = (eventId) => {
    setWebhookForm(prev => {
      const events = prev.events.includes(eventId)
        ? prev.events.filter(e => e !== eventId)
        : [...prev.events, eventId];
      return { ...prev, events };
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-gray-600">Laster bedriftsinnstillinger...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm border-b p-4 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Bedriftsinnstillinger</h2>
        <button 
          onClick={handleClose}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          title="Lukk"
        >
          <SafeIcon icon={FiIcons.FiX} className="w-6 h-6 text-gray-500" />
        </button>
      </div>

      <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
        {/* Sidebar Menu */}
        <div className="w-full md:w-64 bg-white border-r border-gray-200 pt-6">
          <nav className="space-y-1 px-2">
            <button
              onClick={() => setActiveTab('general')}
              className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                activeTab === 'general' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={FiIcons.FiSettings} className="mr-3 flex-shrink-0 h-5 w-5" />
              Generelt
            </button>
            <button
              onClick={() => setActiveTab('compliance')}
              className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                activeTab === 'compliance' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={FiShield} className="mr-3 flex-shrink-0 h-5 w-5" />
              Tilsyn & Compliance
            </button>
            <button
              onClick={() => setActiveTab('dpa')}
              className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                activeTab === 'dpa' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={FiShield} className="mr-3 flex-shrink-0 h-5 w-5" />
              Databehandleravtale
            </button>
            <button
              onClick={() => setActiveTab('api')}
              className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                activeTab === 'api' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={FiIcons.FiCode} className="mr-3 flex-shrink-0 h-5 w-5" />
              API & Integrasjon
            </button>
            <button
              onClick={() => setActiveTab('webhooks')}
              className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                activeTab === 'webhooks' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={FiActivity} className="mr-3 flex-shrink-0 h-5 w-5" />
              Webhooks
            </button>
          </nav>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="max-w-4xl mx-auto space-y-8">
            
            {/* TAB: GENERAL */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <section className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Generell Informasjon</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bedriftsnavn</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Organisasjonsnummer</label>
                      <input
                        type="text"
                        value={formData.org_number}
                        onChange={(e) => setFormData({ ...formData, org_number: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Kontakt-epost (Admin)</label>
                      <input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={handleGeneralSave}
                      disabled={saveLoading}
                      className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      <SafeIcon icon={FiIcons.FiSave} />
                      {saveLoading ? 'Lagrer...' : 'Lagre endringer'}
                    </button>
                  </div>
                </section>

                <section className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Abonnement</h3>
                  <p className="text-gray-600">
                    Gjeldende plan: <strong>{currentCompany?.subscription?.toUpperCase() || 'STANDARD'}</strong>
                  </p>
                  <button onClick={() => navigate('/company/profile')} className="mt-4 text-blue-600 hover:underline">
                    Administrer abonnement &rarr;
                  </button>
                </section>
              </div>
            )}

            {/* TAB: COMPLIANCE */}
            {activeTab === 'compliance' && (
              <div className="space-y-6">
                <ComplianceAuditPackage />
                <InspectionReportGenerator 
                  companyId={currentCompany?.id} 
                  companyData={currentCompany} 
                />
                
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="font-bold text-gray-900 mb-4">Juridisk Status</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-4 bg-green-50 text-green-800 rounded-lg border border-green-100">
                      <SafeIcon icon={FiShield} className="mt-1 text-xl" />
                      <div>
                        <p className="font-bold">Systemet er i samsvar med EUs varslingsdirektiv</p>
                        <p className="text-sm opacity-90">Siste automatiske sjekk: I dag kl. 09:00. Alle lovpålagte funksjoner er aktive.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB: DPA */}
            {activeTab === 'dpa' && (
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Databehandleravtale</h2>
                <DataProcessingAgreement />
              </section>
            )}

            {/* TAB: API */}
            {activeTab === 'api' && (
              <section className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <SafeIcon icon={FiIcons.FiCode} className="w-5 h-5 text-blue-600" />
                      </div>
                      API Tilgang
                    </h3>
                    <p className="text-gray-600 mt-1">
                      Koble varslingssystemet til deres eksisterende verktøy.
                    </p>
                  </div>
                  <button 
                    onClick={() => setShowApiDocs(true)}
                    className="group flex items-center gap-2 px-4 py-2.5 text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all font-medium"
                  >
                    <SafeIcon icon={FiIcons.FiBookOpen} className="w-4 h-4 group-hover:scale-110 transition-transform" />
                    Se Dokumentasjon
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Din API Nøkkel (Secret Key)
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <code className="block w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 font-mono text-sm text-gray-700 break-all">
                          {apiKey}
                        </code>
                      </div>
                      <button 
                        onClick={() => navigator.clipboard.writeText(apiKey)}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:text-blue-600 hover:border-blue-300 rounded-lg transition-all flex items-center gap-2 shadow-sm"
                      >
                        <SafeIcon icon={FiIcons.FiCopy} className="w-5 h-5" />
                        <span className="hidden sm:inline">Kopier</span>
                      </button>
                      <button 
                        onClick={() => setApiKey(`pk_${currentCompany?.id?.substring(0, 8) || 'new'}_${Math.random().toString(36).substring(2, 15)}`)}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:text-blue-600 hover:border-blue-300 rounded-lg transition-all flex items-center gap-2 shadow-sm"
                      >
                        <SafeIcon icon={FiIcons.FiRefreshCw} className="w-5 h-5" />
                        <span className="hidden sm:inline">Ny nøkkel</span>
                      </button>
                    </div>
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                      <SafeIcon icon={FiIcons.FiAlertTriangle} className="w-3 h-3" />
                      Hold denne nøkkelen hemmelig. Den gir full administrativ tilgang til din bedrifts data.
                    </p>
                  </div>
                </div>
              </section>
            )}

            {/* TAB: WEBHOOKS */}
            {activeTab === 'webhooks' && (
              <section className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <div className="p-2 bg-purple-50 rounded-lg">
                        <SafeIcon icon={FiActivity} className="w-5 h-5 text-purple-600" />
                      </div>
                      Webhooks
                    </h3>
                    <p className="text-gray-600 mt-1">
                      Motta varsler i sanntid når nye hendelser oppstår.
                    </p>
                  </div>
                  <button 
                    onClick={() => openWebhookModal()}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 font-medium"
                  >
                    <SafeIcon icon={FiPlus} />
                    Legg til Webhook
                  </button>
                </div>

                <div className="space-y-4">
                  {companyWebhooks.length > 0 ? (
                    companyWebhooks.map(webhook => (
                      <div key={webhook.id} className="border border-gray-200 rounded-xl p-4 hover:border-purple-200 transition-colors bg-white">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                          <div className="flex-1 overflow-hidden">
                            <div className="flex items-center gap-3 mb-1">
                              <span className={`w-2.5 h-2.5 rounded-full ${webhook.active ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                              <h4 className="font-mono text-sm text-gray-900 truncate" title={webhook.url}>{webhook.url}</h4>
                            </div>
                            <div className="flex gap-2 mt-2">
                              {webhook.events.map(evt => (
                                <span key={evt} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded border border-gray-200">
                                  {evt}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            {webhook.lastTriggered && (
                              <div className="text-right hidden sm:block">
                                <p className="text-xs text-gray-500">Sist kjørt</p>
                                <div className="flex items-center gap-1">
                                  <span className={`text-xs font-mono font-medium ${webhook.lastStatus >= 200 && webhook.lastStatus < 300 ? 'text-green-600' : 'text-red-600'}`}>
                                    {webhook.lastStatus}
                                  </span>
                                  <span className="text-xs text-gray-700">
                                    {new Date(webhook.lastTriggered).toLocaleDateString('no-NO')}
                                  </span>
                                </div>
                              </div>
                            )}
                            <div className="flex gap-2">
                              <button 
                                onClick={() => openWebhookModal(webhook)}
                                className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                title="Rediger"
                              >
                                <SafeIcon icon={FiEdit2} />
                              </button>
                              <button 
                                onClick={() => {
                                    if(window.confirm('Er du sikker på at du vil slette denne webhooken?')) {
                                        if (typeof deleteWebhook === 'function') deleteWebhook(webhook.id);
                                    }
                                }}
                                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Slett"
                              >
                                <SafeIcon icon={FiTrash2} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                      <SafeIcon icon={FiGlobe} className="mx-auto text-4xl text-gray-300 mb-2" />
                      <p className="text-gray-500 text-sm">Ingen webhooks konfigurert.</p>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>

      {/* Webhook Modal */}
      <AnimatePresence>
        {showWebhookModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl max-w-lg w-full overflow-hidden"
            >
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <h3 className="text-lg font-bold text-gray-900">
                  {editingWebhook ? 'Rediger Webhook' : 'Ny Webhook'}
                </h3>
                <button onClick={() => setShowWebhookModal(false)} className="text-gray-400 hover:text-gray-600">
                  <SafeIcon icon={FiX} />
                </button>
              </div>
              <form onSubmit={handleWebhookSubmit} className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Endpoint URL</label>
                  <input 
                    type="url" 
                    required
                    placeholder="https://api.ditt-system.no/webhook"
                    value={webhookForm.url}
                    onChange={(e) => setWebhookForm({...webhookForm, url: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Signaturhemmelighet (Secret)</label>
                  <div className="flex gap-2">
                    <input 
                      type="text" 
                      readOnly
                      value={webhookForm.secret}
                      className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg font-mono text-sm text-gray-600"
                    />
                    <button 
                      type="button"
                      onClick={() => setWebhookForm({...webhookForm, secret: generateWebhookSecret()})}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
                      title="Generer ny hemmelighet"
                    >
                      <SafeIcon icon={FiIcons.FiRefreshCw} />
                    </button>
                    <button 
                      type="button"
                      onClick={() => navigator.clipboard.writeText(webhookForm.secret)}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
                      title="Kopier"
                    >
                      <SafeIcon icon={FiIcons.FiCopy} />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Bruk denne til å verifisere `X-HMS-Signature` headeren.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Abonner på hendelser</label>
                  <div className="space-y-2 border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                    {availableEvents.map(evt => (
                      <label key={evt.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={webhookForm.events.includes(evt.id)}
                          onChange={() => toggleEvent(evt.id)}
                          className="rounded text-purple-600 focus:ring-purple-500 border-gray-300"
                        />
                        <span className="text-sm text-gray-700">{evt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <div 
                      className={`w-10 h-6 rounded-full p-1 transition-colors ${webhookForm.active ? 'bg-green-500' : 'bg-gray-300'}`}
                      onClick={() => setWebhookForm({...webhookForm, active: !webhookForm.active})}
                    >
                      <div className={`bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${webhookForm.active ? 'translate-x-4' : 'translate-x-0'}`} />
                    </div>
                    <span className="text-sm font-medium text-gray-700">Aktiv</span>
                  </label>

                  <button 
                    type="button"
                    onClick={handleTestWebhook}
                    disabled={!webhookForm.url || testStatus === 'loading'}
                    className={`text-sm px-3 py-1.5 rounded border transition-colors flex items-center gap-2 ${
                      testStatus === 'success' ? 'border-green-200 bg-green-50 text-green-700' :
                      testStatus === 'error' ? 'border-red-200 bg-red-50 text-red-700' :
                      'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {testStatus === 'loading' ? <SafeIcon icon={FiIcons.FiRefreshCw} className="animate-spin" /> : 
                     testStatus === 'success' ? <SafeIcon icon={FiCheckCircle} /> :
                     testStatus === 'error' ? <SafeIcon icon={FiAlertCircle} /> :
                     <SafeIcon icon={FiActivity} />}
                    {testStatus === 'loading' ? 'Tester...' : 
                     testStatus === 'success' ? 'Levert OK' : 
                     testStatus === 'error' ? 'Feilet' : 'Test Webhook'}
                  </button>
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-100">
                  <button 
                    type="button" 
                    onClick={() => setShowWebhookModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Avbryt
                  </button>
                  <button 
                    type="submit" 
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Lagre
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showApiDocs && (
          <ApiDocumentationModal onClose={() => setShowApiDocs(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CompanySettings;